// Ask about this (JEV.md §4): tools/jev.py ask_requests / ask_select / ask_prompt and the OpenAI Decisions translation
// (OPENAI.md §7), ported exactly.

export const ASK_LEVELS = ["Unrelated to the question", "Same topic, but doesn't help answer the question",
  "Partially answers the question or gives supporting facts", "Directly answers the question with specific facts"];
const ASK_WINDOW = 120, ASK_TOP = 6, ASK_MIN_SCORE = 1.5, ASK_MIN_CHARS = 40, ASK_NEAR = 2;
export const JEV_MODEL = "jev-latest";
export const ASK_DEFAULT_MODEL = "google/gemini-3.8-flash";
export const DECISIONS_MODEL = "gpt-6-luna";

const len = (s: string) => Array.from(s).length;

export function askRequests(question: string, paragraphs: string[]): any[] {
  const ids = paragraphs.map((p, i) => [p, i] as const).filter(([p]) => len(p) >= ASK_MIN_CHARS).map(([, i]) => i);
  const bodies: any[] = [];
  for (let s = 0; s < ids.length; s += ASK_WINDOW) {
    const w = ids.slice(s, s + ASK_WINDOW);
    const passages: Record<string, string> = {}, questions: Record<string, any> = {};
    for (const i of w) {
      passages[`p${i}`] = paragraphs[i];
      questions[`p${i}`] = { type: "score", instructions: `How well does \`passages.p${i}\` help answer \`question\`?`, criteria: ASK_LEVELS };
    }
    bodies.push({ state: { question, passages }, model: JEV_MODEL, questions });
  }
  return bodies;
}

export function askSelect(paragraphs: string[], answers: Record<string, any> | null, position: number): number[] {
  const n = paragraphs.length;
  const range = (lo: number, hi: number) => (lo < hi ? Array.from({ length: hi - lo }, (_, i) => lo + i) : []);
  const near = range(Math.max(0, position - ASK_NEAR), Math.min(n, position + 1));
  if (!answers) return [...new Set([...range(0, Math.min(3, n)), ...range(Math.max(0, position - 9), Math.min(n, position + 1))])].sort((a, b) => a - b);
  const scored: [number, number][] = [];
  for (const [k, a] of Object.entries(answers)) {
    const v = a && typeof a === "object" ? a.score : undefined;
    if (typeof v === "number" && /^p\d+$/.test(k) && parseInt(k.slice(1), 10) < n) scored.push([parseInt(k.slice(1), 10), v]);
  }
  const top = scored.sort((x, y) => (y[1] - x[1]) || (x[0] - y[0])).filter(([, v]) => v >= ASK_MIN_SCORE).slice(0, ASK_TOP).map(([i]) => i);
  return [...new Set([...top, ...near])].sort((a, b) => a - b);
}

export const ASK_SYSTEM = `You answer a listener's question about the text they are listening to. Your answer will be read aloud.
Answer in one to four short sentences of plain spoken English: no markdown, lists, links or citations.
Use only the passages given. If they don't answer the question, say so briefly and say what the text does cover.
For a book, never reveal anything beyond the passages (the listener hasn't read further).`;

export function askPrompt(question: string, title: string, author: string | null, isBook: boolean, paragraphs: string[], selected: number[], position: number) {
  const lines = [`Title: ${title}`, `Author: ${author || "unknown"}`,
    `The listener is at paragraph ${position} of this ${isBook ? "book (so far)" : "article"}.`, "", "Passages:",
    ...selected.map((i) => `[${i}] ${paragraphs[i]}`), "", `Question: ${question}`];
  return {
    messages: [{ role: "system", content: ASK_SYSTEM }, { role: "user", content: lines.join("\n") }],
    temperature: 0.3, max_tokens: 400, reasoning: { effort: "minimal" }, usage: { include: true },
  };
}

export function toDecisions(body: any): any {
  const questions = Object.entries(body.questions as Record<string, any>).map(([name, q]) => {
    const d: any = { name, type: q.type === "noul" ? "predicate" : q.type === "choice" ? "choice" : "score", instructions: q.instructions };
    if (q.type === "choice") d.choices = Object.entries(q.criteria).map(([value, description]) => ({ value, description }));
    else if (q.type === "score") d.levels = (q.criteria as string[]).map((t) => ({ label: t, description: t }));
    return d;
  });
  return { model: DECISIONS_MODEL, input: [{ role: "user", content: [{ type: "input_json", data: body.state }] }], questions };
}

export function fromDecisions(reply: any): { answers: Record<string, any> } {
  const answers: Record<string, any> = {};
  for (const a of reply?.answers || []) {
    const t = a.type, name = a.name;
    if (t === "predicate") answers[name] = { type: "noul", noul: a.probability };
    else if (t === "choice") answers[name] = { type: "choice", choice: a.choice, confidence: a.confidence,
      probabilities: Object.fromEntries((a.probabilities || []).map((p: any) => [p.value, p.probability])) };
    else if (t === "score") answers[name] = { type: "score", score: a.score, confidence: a.confidence,
      probabilities: Object.fromEntries((a.probabilities || []).map((p: any) => [p.label, p.probability])) };
  }
  return { answers };
}
